import React, { createContext, ReactNode, useEffect, useState, useMemo, useCallback } from "react";
import useAlert from "../Alerts/useAlert";
import { UserPreferences } from "../Types/UserPreferences";
import { client } from "../Utilities/AuthUtils";
import { useHttpUtils } from "../Utilities/useHttpUtils";

interface UserPreferencesContextInterface {
    loading?: boolean,
    userPreferences: UserPreferences,
    setPreferredBusinessUnits: (businessUnits: string[]) => void,
    setPreferredLines: (lines: string[]) => void,
    setIncludeIndirectTransfers: (val: boolean) => void,
    setActiveSections: (activeSections: string[]) => void,
    setPreferredTimeFormat: (val: boolean) => void,
    saveUserPreference: (accessor: string, val: any) => void,
}

export const UserPreferencesContext = createContext<UserPreferencesContextInterface>({
    loading: true,
    userPreferences: {},
    setPreferredBusinessUnits: () => {},
    setPreferredLines: () => {},
    setIncludeIndirectTransfers: () => {},
    setActiveSections: () => {},
    setPreferredTimeFormat: () => {},
    saveUserPreference: () => {},
});

export function UserPreferencesProvider({ children }: {children:ReactNode}) {
    const account = client.getAllAccounts()[0];
    const { fetchDataAuthenticated } = useHttpUtils();
    const { setAlert } = useAlert();
    const [userPreferences, setUserPreferences] = useState<UserPreferences>({});
    const [loading, setLoading] = useState<boolean>();

    useEffect(() => {
        if (account?.username) {
            setLoading(true);

            fetchDataAuthenticated("api/v1/userpreferences?username=" + account.username)
                .then(async (response) => {

                    if (response.status === 204) {
                        return null;
                    }

                    if (response.ok) {
                        return response.json();
                    }

                    throw new Error(await response.json());
                })
                .then(data => {
                    setUserPreferences(data?.data);
                })
                .catch(error => {
                    setAlert("danger", "Error", "Failed to get user preferences. " + error.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [account.username, fetchDataAuthenticated, setAlert]);

    const saveChanges = useCallback(async (updatedUserPreferences: any) => {
        await fetchDataAuthenticated("api/v1/userpreferences/" + account.username, "PUT", JSON.stringify({ data: updatedUserPreferences }))
            .then(async (response) => {
                if (response.ok) {
                    return;
                }

                throw new Error(await response.json());
            })
            .then(() => {
                console.log("Saved user preferences");
            })
            .catch(error => {
                setAlert("danger", "Error", "Failed to save user preferences. " + error.message);
            });
    } ,[account.username, fetchDataAuthenticated, setAlert]);

    const getUserPreferencesToUpdate = useCallback(() => {
        let updatedUserPreferences: UserPreferences = { ...userPreferences };

        // set defaults if not exist
        updatedUserPreferences.lotList = updatedUserPreferences.lotList || {};
        updatedUserPreferences.lotList.defaultBusinessUnits = updatedUserPreferences.lotList.defaultBusinessUnits || [];
        updatedUserPreferences.lotList.defaultLines = updatedUserPreferences.lotList.defaultLines || [];
        updatedUserPreferences.lotDetail = updatedUserPreferences.lotDetail || {};
        updatedUserPreferences.lotDetail.activeSections = updatedUserPreferences.lotDetail.activeSections || [];
        updatedUserPreferences.lotDetail.genealogy = updatedUserPreferences.lotDetail.genealogy || { includeIndirectTransfers: false };
        updatedUserPreferences.lotDetail.showLotSplitHelpText = updatedUserPreferences.lotDetail.showLotSplitHelpText || false;
        updatedUserPreferences.use24hr = updatedUserPreferences.use24hr || true;

        return updatedUserPreferences;
    }, [userPreferences]);

    const setPreferredBusinessUnits = useCallback((businessUnits: string[]) => {
        let updatedUserPreferences = getUserPreferencesToUpdate();
        updatedUserPreferences.lotList!.defaultBusinessUnits = businessUnits.length > 0 ? [...businessUnits] : null;

        setUserPreferences(updatedUserPreferences);
        saveChanges(updatedUserPreferences);
    }, [getUserPreferencesToUpdate, saveChanges]);

    const setPreferredLines = useCallback((lines: string[]) => {
        let updatedUserPreferences = getUserPreferencesToUpdate();
        updatedUserPreferences.lotList!.defaultLines = lines.length > 0 ? [...lines] : null;

        setUserPreferences(updatedUserPreferences);
        saveChanges(updatedUserPreferences);
    }, [getUserPreferencesToUpdate, saveChanges]);

    const setActiveSections = useCallback(async (activeSections: string[]) => {
        let updatedUserPreferences = getUserPreferencesToUpdate();
        updatedUserPreferences.lotDetail!.activeSections = [...activeSections];

        setUserPreferences(updatedUserPreferences);
        saveChanges(updatedUserPreferences);
    }, [getUserPreferencesToUpdate, saveChanges]);

    const setIncludeIndirectTransfers = useCallback((val: boolean) => {
        let updatedUserPreferences = getUserPreferencesToUpdate();
        updatedUserPreferences.lotDetail!.genealogy!.includeIndirectTransfers = val;

        setUserPreferences(updatedUserPreferences);
        saveChanges(updatedUserPreferences);
    }, [getUserPreferencesToUpdate, saveChanges]);

    const setPreferredTimeFormat = useCallback((val: boolean) => {
        let updatedUserPreferences = getUserPreferencesToUpdate();
        updatedUserPreferences.use24hr = val;

        setUserPreferences(updatedUserPreferences);
        saveChanges(updatedUserPreferences);
    }, [getUserPreferencesToUpdate, saveChanges]);

    /**
     * Save a change to user preferences by using a generic save method
     * @param accessor A string in Dot Notation format to access the fields that we want to update on the user preferences object
     *  @example accessor: "lotDetail.genealogy.showTransfers", val: true
     *  @example accessor: "use24hr", val: true
     * @param val the updated val we want to set the field to
     */
    const saveUserPreference = useCallback((accessor: string, val: any) => {
        // Adding the type safety to the accessor string is a can of worms which really requires an upgrade to TS 4.1.3
        // which seems to come with a few breaking changes
        let updatedUserPreferences: any = getUserPreferencesToUpdate();
        let fields = accessor.split(".");
        if (fields.length === 1) {
            updatedUserPreferences[fields[0]] = val;
        } else if (fields.length > 1) {
            let nestedFields = fields.slice(0, -1);
            let lastField = fields[fields.length - 1];
            let nestedObject = nestedFields.reduce((current, accessor) => { return current[accessor]; }, updatedUserPreferences as any);
            nestedObject[lastField] = val;
        }

        setUserPreferences(updatedUserPreferences);
        saveChanges(updatedUserPreferences);
    }, [getUserPreferencesToUpdate, saveChanges]);

    const contextValue = useMemo(() => ({ loading, userPreferences, setPreferredBusinessUnits, setPreferredLines, setIncludeIndirectTransfers, setActiveSections, setPreferredTimeFormat, saveUserPreference }),
        [loading, saveUserPreference, setActiveSections, setIncludeIndirectTransfers, setPreferredBusinessUnits, setPreferredLines, setPreferredTimeFormat, userPreferences]);

    return (
        <UserPreferencesContext.Provider value={contextValue}>
            {children}
        </UserPreferencesContext.Provider>
    );
}
